import { useSearchParams } from "react-router";
import { normalizeRangeKey, TIME_RANGE_LABEL } from "@colab/shared";
import { PartnerAnalyticsView } from "@/components/shared/analytics/PartnerAnalyticsView";
import { TimeRangeSelector } from "@/components/shared/analytics/TimeRangeSelector";
import { useDealPipeline } from "./use-deal-pipeline";
import { useContentEngagement } from "./use-content-engagement";
import { useNotificationEngagement } from "./use-notification-engagement";
import { useActivityTimeline } from "./use-activity-timeline";

// Phase 7 sub-bucket 6 — partner analytics. Page is the orchestrator: reads
// `?range=` from the URL via react-router, fires four query hooks
// (pipeline / content-engagement / notifications / activity), and passes
// the raw query results into the shared <PartnerAnalyticsView>. The view
// itself stays framework-agnostic so admin sub-bucket 10 can reuse it for
// the admin partner-detail analytics tab without modification.
//
// EUR-only ARR is enforced at the service layer
// (apps/api/src/services/analytics/queries.ts) — the page just renders the
// number that arrives. The brief's optional "custom date range" is not
// supported (legacy didn't ship it; out of scope for parity).
export function AnalyticsPage() {
  const [params, setParams] = useSearchParams();
  const range = normalizeRangeKey(params.get("range"));

  const pipeline = useDealPipeline(range);
  const content = useContentEngagement(range);
  const notifications = useNotificationEngagement(range);
  const timeline = useActivityTimeline(range);

  return (
    <div>
      <header className="mb-7 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Performance
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Analytics
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Your pipeline, content engagement, and recent activity.
          </p>
        </div>
        <TimeRangeSelector
          value={range}
          onChange={(next) => {
            const sp = new URLSearchParams(params);
            sp.set("range", next);
            setParams(sp, { replace: true });
          }}
        />
      </header>
      <PartnerAnalyticsView
        pipeline={pipeline}
        content={content}
        notifications={notifications}
        timeline={timeline}
        rangeLabel={TIME_RANGE_LABEL[range]}
      />
    </div>
  );
}
